// Reemplazo en JS de los confirm/alert/prompt nativos del browser.
//
// Por qué existe esto:
//   Los modales nativos del browser (window.confirm/alert/prompt) son
//   feos, no se pueden estilar y rompen la consistencia visual del
//   admin (tema oscuro violeta). El user pidió "que no haya nada por
//   defecto del sistema". Estos hooks devuelven Promise<...> y muestran
//   un modal React con el estilo del proyecto.
//
// Patrón de uso:
//   const dialogs = useDialogs();
//   // confirm:
//   if (await dialogs.confirm({
//     title: "Borrar producto",
//     message: "Esta acción no se puede deshacer.",
//     variant: "destructive",
//   })) { ... }
//   // alert:
//   await dialogs.alert("Configuración guardada.");
//   // prompt:
//   const notes = await dialogs.prompt("Notas opcionales:");
//
// Como los modales viven en el árbol React (no en window), el caller
// debe estar dentro de <DialogsProvider /> (wrappear en App.tsx).
//
// Solo UN dialog a la vez. Si llamás .confirm() mientras hay otro
// abierto, el actual se reemplaza (los nativos del browser hacen lo
// mismo: cada uno bloquea hasta cerrar).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react";
import { Modal } from "../components/ui/Modal";

// ───────────── Tipos ─────────────

export type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "destructive" = botón rojo (borrar, desactivar, etc.). */
  variant?: "default" | "destructive";
};

export type AlertOptions = {
  title?: string;
  message: string;
  variant?: "info" | "error" | "success";
};

export type PromptOptions = {
  title?: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

export type ToastOptions = {
  message: string;
  variant?: "info" | "error" | "success";
  /** ms antes de auto-cerrar. Default 3000. 0 = no auto-cierra. */
  durationMs?: number;
  /** Accion opcional (ej "Deshacer"). Al tocarla se ejecuta onAction y
   *  se cierra el toast. */
  action?: { label: string; onAction: () => void };
};

type ToastItem = {
  id: number;
  message: string;
  variant: "info" | "error" | "success";
  action?: { label: string; onAction: () => void };
};

type DialogState =
  | {
      kind: "confirm";
      options: ConfirmOptions;
      resolve: (v: boolean) => void;
    }
  | {
      kind: "alert";
      options: AlertOptions;
      resolve: () => void;
    }
  | {
      kind: "prompt";
      options: PromptOptions;
      resolve: (v: string | null) => void;
    }
  | null;

type DialogsApi = {
  confirm: (opts: ConfirmOptions | string) => Promise<boolean>;
  alert: (opts: AlertOptions | string) => Promise<void>;
  prompt: (opts: PromptOptions | string) => Promise<string | null>;
  /** Notificacion no bloqueante, auto-dismiss. Para feedback de
   *  guardado, "copiado", etc. — visible desde cualquier scroll, a
   *  diferencia de un banner perdido al pie de un form largo. */
  toast: (opts: ToastOptions | string) => void;
};

const DialogsContext = createContext<DialogsApi | null>(null);

// ───────────── Provider ─────────────

export function DialogsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastIdRef = useRef(0);

  const toast = useCallback((opts: ToastOptions | string) => {
    const o = typeof opts === "string" ? { message: opts } : opts;
    const id = ++toastIdRef.current;
    setToasts((prev) => [
      ...prev,
      {
        id,
        message: o.message,
        variant: o.variant ?? "success",
        action: o.action,
      },
    ]);
    // Con accion (ej Deshacer) damos mas tiempo por default.
    const duration = o.durationMs ?? (o.action ? 6000 : 3000);
    if (duration > 0) {
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  function dismissToast(id: number) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  const confirm = useCallback(
    (opts: ConfirmOptions | string): Promise<boolean> => {
      const options = typeof opts === "string" ? { message: opts } : opts;
      return new Promise<boolean>((resolve) => {
        setState({ kind: "confirm", options, resolve });
      });
    },
    [],
  );

  const alert = useCallback(
    (opts: AlertOptions | string): Promise<void> => {
      const options = typeof opts === "string" ? { message: opts } : opts;
      return new Promise<void>((resolve) => {
        setState({ kind: "alert", options, resolve });
      });
    },
    [],
  );

  const prompt = useCallback(
    (opts: PromptOptions | string): Promise<string | null> => {
      const options = typeof opts === "string" ? { message: opts } : opts;
      return new Promise<string | null>((resolve) => {
        setState({ kind: "prompt", options, resolve });
      });
    },
    [],
  );

  function close() {
    setState(null);
  }

  function resolveConfirm(value: boolean) {
    if (state?.kind === "confirm") state.resolve(value);
    close();
  }
  function resolveAlert() {
    if (state?.kind === "alert") state.resolve();
    close();
  }
  function resolvePrompt(value: string | null) {
    if (state?.kind === "prompt") state.resolve(value);
    close();
  }

  return (
    <DialogsContext.Provider value={{ confirm, alert, prompt, toast }}>
      {children}

      {/* Contenedor de toasts — fixed bottom-right, apila los activos.
          z por encima de todo el contenido pero no bloquea (pointer
          events solo en cada toast para el boton de cerrar). */}
      {toasts.length > 0 && (
        <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
          {toasts.map((t) => {
            const Icon =
              t.variant === "error"
                ? AlertCircle
                : t.variant === "success"
                ? CheckCircle
                : Info;
            const color =
              t.variant === "error"
                ? "text-red-400"
                : t.variant === "success"
                ? "text-emerald-400"
                : "text-[#a78bfa]";
            return (
              <div
                key={t.id}
                role="status"
                className="pointer-events-auto flex items-center gap-2 rounded-lg border border-white/10 bg-[#1a1540] px-4 py-3 text-sm shadow-2xl"
              >
                <Icon className={`h-4 w-4 shrink-0 ${color}`} />
                <span className="flex-1">{t.message}</span>
                {t.action && (
                  <button
                    type="button"
                    onClick={() => {
                      t.action!.onAction();
                      dismissToast(t.id);
                    }}
                    className="shrink-0 rounded-md bg-white/10 px-2.5 py-1 text-xs font-bold text-[#c4b5fd] hover:bg-white/20"
                  >
                    {t.action.label}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => dismissToast(t.id)}
                  aria-label="Cerrar"
                  className="shrink-0 rounded p-0.5 opacity-50 hover:bg-white/10 hover:opacity-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {state?.kind === "confirm" && (
        <ConfirmDialog
          options={state.options}
          onConfirm={() => resolveConfirm(true)}
          onCancel={() => resolveConfirm(false)}
        />
      )}
      {state?.kind === "alert" && (
        <AlertDialog options={state.options} onClose={resolveAlert} />
      )}
      {state?.kind === "prompt" && (
        <PromptDialog
          options={state.options}
          onSubmit={(v) => resolvePrompt(v)}
          onCancel={() => resolvePrompt(null)}
        />
      )}
    </DialogsContext.Provider>
  );
}

export function useDialogs(): DialogsApi {
  const ctx = useContext(DialogsContext);
  if (!ctx) {
    throw new Error(
      "useDialogs debe usarse dentro de <DialogsProvider /> (wrappeá tu app)",
    );
  }
  return ctx;
}

// ───────────── Componentes internos ─────────────

function ConfirmDialog({
  options,
  onConfirm,
  onCancel,
}: {
  options: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const {
    title,
    message,
    confirmLabel = "Confirmar",
    cancelLabel = "Cancelar",
    variant = "default",
  } = options;

  // Escape cierra como cancelar (igual a confirm() nativo).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const isDestructive = variant === "destructive";
  const Icon = isDestructive ? AlertTriangle : Info;
  const iconColor = isDestructive ? "text-red-400" : "text-[#a78bfa]";
  const confirmBg = isDestructive
    ? "bg-red-600 hover:bg-red-500"
    : "bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] hover:opacity-90";

  return (
    <Modal open onClose={onCancel} closeOnBackdrop>
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <Icon className={`mt-0.5 h-6 w-6 shrink-0 ${iconColor}`} />
          <div className="flex-1">
            {title && (
              <h2 className="font-heading text-xl font-bold">{title}</h2>
            )}
            <p
              className={`whitespace-pre-wrap text-sm opacity-90 ${
                title ? "mt-2" : ""
              }`}
            >
              {message}
            </p>
          </div>
        </div>
        <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg bg-white/10 px-5 py-2.5 font-medium hover:bg-white/20"
            autoFocus={!isDestructive}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-lg px-5 py-2.5 font-bold text-white transition ${confirmBg}`}
            autoFocus={isDestructive ? false : undefined}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AlertDialog({
  options,
  onClose,
}: {
  options: AlertOptions;
  onClose: () => void;
}) {
  const { title, message, variant = "info" } = options;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" || e.key === "Enter") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const Icon =
    variant === "error"
      ? AlertCircle
      : variant === "success"
      ? CheckCircle
      : Info;
  const iconColor =
    variant === "error"
      ? "text-red-400"
      : variant === "success"
      ? "text-emerald-400"
      : "text-[#a78bfa]";

  return (
    <Modal open onClose={onClose} closeOnBackdrop>
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <Icon className={`mt-0.5 h-6 w-6 shrink-0 ${iconColor}`} />
          <div className="flex-1">
            {title && (
              <h2 className="font-heading text-xl font-bold">{title}</h2>
            )}
            <p
              className={`whitespace-pre-wrap text-sm opacity-90 ${
                title ? "mt-2" : ""
              }`}
            >
              {message}
            </p>
          </div>
        </div>
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-5 py-2.5 font-bold text-white hover:opacity-90"
            autoFocus
          >
            Entendido
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PromptDialog({
  options,
  onSubmit,
  onCancel,
}: {
  options: PromptOptions;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const {
    title,
    message,
    placeholder,
    defaultValue = "",
    confirmLabel = "Aceptar",
    cancelLabel = "Cancelar",
  } = options;

  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <Modal open onClose={onCancel} closeOnBackdrop>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(value);
        }}
      >
        {title && <h2 className="font-heading text-xl font-bold">{title}</h2>}
        <p
          className={`whitespace-pre-wrap text-sm opacity-90 ${
            title ? "" : ""
          }`}
        >
          {message}
        </p>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
        <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg bg-white/10 px-5 py-2.5 font-medium hover:bg-white/20"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            className="rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-5 py-2.5 font-bold text-white hover:opacity-90"
          >
            {confirmLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
